import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AboutUs from "@/pages/AboutUs";
import Publications from "@/pages/Publications";
import TestimonialsPage from "@/pages/TestimonialsPage";
import MethodologicalForm from "@/pages/MethodologicalForm";
import TrainingForm from "@/pages/TrainingForm";
import GrantForm from "@/pages/GrantForm";
import DataAnalysisForm from "@/pages/DataAnalysisForm";
import GeneralEnquiryForm from "@/pages/GeneralEnquiryForm";
import ThesisDissertationForm from "@/pages/ThesisDissertationForm";
import ClinicalResearchForm from "@/pages/ClinicalResearchForm";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);

  useEffect(() => {
    const processAuth = async () => {
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        setIsProcessingAuth(true);
        const sessionId = sessionIdMatch[1];

        try {
          const response = await axios.post(
            `${API}/auth/session`,
            {},
            {
              headers: { "X-Session-ID": sessionId }
            }
          );
          
          // Store token in localStorage
          if (response.data.session_token) {
            localStorage.setItem('access_token', response.data.session_token);
          }
          
          setUser(response.data.user);
          // Clean URL
          window.history.replaceState(null, "", window.location.pathname);
        } catch (error) {
          console.error("Auth error:", error);
        } finally {
          setIsProcessingAuth(false);
          setLoading(false);
        }
      } else {
        // Check existing session from localStorage
        const token = localStorage.getItem('access_token');
        
        if (token) {
          try {
            const response = await axios.get(`${API}/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            setUser(response.data);
          } catch (error) {
            // Token invalid or expired, clear it
            localStorage.removeItem('access_token');
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      }
    };

    processAuth();
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem('access_token', token);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem('access_token');
      setUser(null);
    }
  };

  if (loading || isProcessingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
            <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
            <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/publications" element={<Publications />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route
              path="/dashboard"
              element={user ? <Dashboard /> : <Navigate to="/" />}
            />
            <Route
              path="/admin"
              element={
                user?.role === "admin" ? <AdminDashboard /> : <Navigate to="/" />
              }
            />
            <Route
              path="/submit/methodological"
              element={user ? <MethodologicalForm /> : <Navigate to="/" />}
            />
            <Route
              path="/submit/training"
              element={user ? <TrainingForm /> : <Navigate to="/" />}
            />
            <Route
              path="/submit/grant"
              element={user ? <GrantForm /> : <Navigate to="/" />}
            />
            <Route
              path="/submit/data-analysis"
              element={user ? <DataAnalysisForm /> : <Navigate to="/" />}
            />
            <Route
              path="/submit/general-enquiry"
              element={<GeneralEnquiryForm />}
            />
            <Route
              path="/submit/thesis-dissertation"
              element={user ? <ThesisDissertationForm /> : <Navigate to="/login" />}
            />
            <Route
              path="/submit/clinical-research"
              element={user ? <ClinicalResearchForm /> : <Navigate to="/login" />}
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;